import { Action, createHook, createStore } from 'react-sweet-state'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { t } from 'i18next'

import {
    deleteResponse,
    deleteResponseContent,
    patchResponse,
    postResponseContent,
    putResponseContent,
} from '../api/responses'
import { ISaveError } from '../types/error'

export enum ConditionNameEnum {
    LANGUAGE = 'user_language',
}

export enum ConditionTypeEnum {
    SLOT = 'slot',
}

interface ICondition {
    name: ConditionNameEnum
    type: ConditionTypeEnum
    value: string
}

export interface IResponseContent {
    image?: string
    text?: string
    _id?: string
    yaml?: string
    condition?: ICondition[]
}

export interface IResponse {
    used: boolean
    name: string
    intent?: string
}

interface ResponsesState {
    responses: IResponse[]
    currentResponseId: string
    responseContent: IResponseContent[]
    newResponseId: string
    searchValue: string
    isKeyAdding: boolean
    isResponseAdding: boolean
}

const responsesInitialState: ResponsesState = {
    responses: [],
    responseContent: [],
    currentResponseId: '',
    newResponseId: '',
    searchValue: '',
    isKeyAdding: false,
    isResponseAdding: false,
}

const responsesActions = {
    setResponses:
        (responses: IResponse[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({
                responses,
            })
        },
    deleteResponse:
        (responseToDelete: string): Action<ResponsesState> =>
        ({ setState, getState }) => {
            deleteResponse(responseToDelete)

            const { currentResponseId, responses, newResponseId } = getState()

            const filteredResponses = responses.filter(
                (response) => response.name !== responseToDelete
            )

            if (currentResponseId === responseToDelete) {
                const [defaultResponse] = filteredResponses

                setState({
                    currentResponseId: defaultResponse.name,
                })
            }

            setState({
                responses: filteredResponses,
            })

            if (newResponseId) {
                setState({
                    newResponseId: '',
                })
            }
        },
    editResponseName:
        (
            oldResponseName: string,
            newResponseName: string
        ): Action<ResponsesState> =>
        ({ setState, getState }) => {
            patchResponse(oldResponseName, newResponseName)

            const clonedResponses = structuredClone(getState().responses)

            const responseToEditIndex = clonedResponses.findIndex(
                (response) => response.name === oldResponseName
            )

            clonedResponses[responseToEditIndex].name = newResponseName

            setState({
                responses: clonedResponses,
            })
        },
    setCurrentResponseId:
        (currentResponseId: string): Action<ResponsesState> =>
        ({ setState }) => {
            setState({
                currentResponseId,
            })
        },
    setResponseContent:
        (responseContent: IResponseContent[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({
                responseContent,
            })
        },
    addResponse:
        (responseName: string): Action<ResponsesState> =>
        ({ setState, getState }) => {
            const { responses } = getState()

            setState({
                responses: [{ used: false, name: responseName }, ...responses],
                newResponseId: responseName,
            })
        },
    addResponseContent:
        ({ text }: { text: string }): Action<ResponsesState> =>
        async ({ getState, setState }) => {
            const { currentResponseId, responseContent, newResponseId } =
                getState()

            const newResponseContent = await postResponseContent(
                newResponseId || currentResponseId,
                {
                    text,
                    condition: [
                        {
                            name: ConditionNameEnum.LANGUAGE,
                            type: ConditionTypeEnum.SLOT,
                            value: 'pl',
                        },
                    ],
                }
            ).then((res) => res.data)

            setState({
                responseContent: [newResponseContent, ...responseContent],

                newResponseId: '',
            })

            if (newResponseId) {
                setState({ currentResponseId: newResponseId })
            }
        },
    deleteResponseContent:
        (responseContentId: string): Action<ResponsesState> =>
        ({ getState, setState }) => {
            deleteResponseContent(responseContentId)

            setState({
                responseContent: getState().responseContent.filter(
                    (message) => message._id !== responseContentId
                ),
            })
        },
    editResponseContent:
        ({
            _id,
            condition,
            text,
            image,
        }: IResponseContent): Action<ResponsesState> =>
        async ({ getState, setState }) => {
            const { responseContent, currentResponseId } = structuredClone(
                getState()
            )

            const newResponseContent = await putResponseContent(
                currentResponseId,
                {
                    _id,
                    condition,
                    text,
                    image,
                }
            ).then((res) => res.data)

            const responseContentIndex = responseContent.findIndex(
                (message) => message._id === newResponseContent._id
            )
            responseContent[responseContentIndex] = newResponseContent

            setState({
                responseContent,
            })
        },
    editResponseYaml:
        ({ _id, yaml }: IResponseContent): Action<ResponsesState> =>
        async ({ getState, setState }) => {
            const { responseContent, currentResponseId } = structuredClone(
                getState()
            )

            const newResponseContent = await putResponseContent(
                currentResponseId,
                {
                    _id,
                    yaml,
                }
            )
                .then((res) => res.data)
                .catch((error: AxiosError) => {
                    const errorData = error.response?.data as ISaveError

                    toast(
                        `${t(errorData.error)}: ${t(errorData.invalidValue)}`,
                        {
                            type: 'error',
                        }
                    )

                    throw new Error()
                })

            const responseContentIndex = responseContent.findIndex(
                (message) => message._id === newResponseContent._id
            )
            responseContent[responseContentIndex] = newResponseContent

            setState({
                responseContent,
            })
        },
    setSearchValue:
        (searchValue: string): Action<ResponsesState> =>
        ({ setState }) => {
            setState({
                searchValue,
            })
        },
    switchIsKeyAdding:
        (): Action<ResponsesState> =>
        ({ setState, getState }) => {
            setState({
                isKeyAdding: !getState().isKeyAdding,
            })
        },
    switchIsResponseAdding:
        (): Action<ResponsesState> =>
        ({ setState, getState }) => {
            setState({
                isResponseAdding: !getState().isResponseAdding,
            })
        },
}

const Store = createStore({
    initialState: responsesInitialState,
    actions: responsesActions,
})

export const useResponsesStore = createHook(Store)
